import { getPostsApi } from '@/api-calls/posts/get'
import { useAuth } from '@/auth/auth-context'
import { ContainerWithSidebar } from '@/components/header/container'
import { useSite } from '@/hooks/use-site'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageTransition,
} from '@blogging/ui'
import { useFetch } from '@ngtrvu/use-api'
import { formatDistance } from 'date-fns'
import {
  ArrowRight,
  ExternalLink,
  FileText,
  LayoutTemplate,
  Paintbrush,
  Plus,
  Settings,
} from 'lucide-react'
import { Link } from 'react-router-dom'

export function SiteDashboardPage() {
  const { user } = useAuth()
  const { isLoading: isLoadingSite, siteId, siteUrlWithScheme } = useSite()

  const getFirstName = (name: string) => {
    return name.split(' ')[0]
  }

  const { data: posts, isLoading } = useFetch(getPostsApi, {
    resourceName: 'posts',
    initialParams: {
      siteId: siteId as string,
    },
    autoLoad: true,
  })

  const isLoadingData = isLoading || isLoadingSite

  // check if the user is first time user by join date is today
  const isFirstTimeLoggedIn =
    user?.createdAt &&
    user?.createdAt.toDateString() === new Date().toDateString()
  const firstName = getFirstName(user?.name || '')

  if (isLoadingData) {
    return (
      <ContainerWithSidebar>
        <div className="flex h-[50vh] w-full items-center justify-center">
          <PageTransition isLoading={true}>Loading dashboard...</PageTransition>
        </div>
      </ContainerWithSidebar>
    )
  }

  return (
    <ContainerWithSidebar>
      <PageTransition isLoading={false}>
        <div className="flex flex-col gap-8">
          {/* Header Section */}
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl font-bold tracking-tight">
              {isFirstTimeLoggedIn
                ? `Welcome to blogging.dev, ${firstName}`
                : `Welcome back, ${firstName}`}
            </h1>
            <p className="text-muted-foreground text-lg">
              Manage your blog content and site settings from one place.
            </p>
          </div>

          {/* Quick Actions Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="hover:bg-accent/50 transition-colors cursor-pointer group">
              <Link to={`/${siteId}/editor/post/new`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    New Post
                  </CardTitle>
                  <Plus className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-muted-foreground">
                    Draft a new story for your readers
                  </div>
                </CardContent>
              </Link>
            </Card>
            <Card className="hover:bg-accent/50 transition-colors cursor-pointer group">
              <a
                href={siteUrlWithScheme || '#'}
                target="_blank"
                rel="noreferrer"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    View Site
                  </CardTitle>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-muted-foreground">
                    Visit your live website
                  </div>
                </CardContent>
              </a>
            </Card>
            <Card className="hover:bg-accent/50 transition-colors cursor-pointer group">
              <Link to={`/${siteId}/settings/design`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Design</CardTitle>
                  <Paintbrush className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-muted-foreground">
                    Customize fonts, colors, and layout
                  </div>
                </CardContent>
              </Link>
            </Card>
            <Card className="hover:bg-accent/50 transition-colors cursor-pointer group">
              <Link to={`/${siteId}/settings`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Settings
                  </CardTitle>
                  <Settings className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-muted-foreground">
                    Configure general site options
                  </div>
                </CardContent>
              </Link>
            </Card>
          </div>

          <div className="grid gap-8 md:grid-cols-7">
            {/* Recent Posts Section */}
            <Card className="md:col-span-4 lg:col-span-5">
              <CardHeader>
                <CardTitle>Recent Posts</CardTitle>
                <CardDescription>
                  Your latest articles and drafts.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {posts && posts.length > 0 ? (
                    posts.slice(0, 5).map((post: any) => (
                      <div
                        key={post.id}
                        className="flex items-center justify-between group"
                      >
                        <div className="space-y-1">
                          <Link
                            to={`/${siteId}/editor/post/${post.id}`}
                            className="font-medium hover:underline flex items-center gap-2"
                          >
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            {post.title || 'Untitled Post'}
                          </Link>
                          <div className="text-sm text-muted-foreground pl-6">
                            Updated{' '}
                            {formatDistance(
                              post.updatedAt || new Date(),
                              new Date(),
                              {
                                addSuffix: true,
                              },
                            )}
                          </div>
                        </div>
                        <Link to={`/${siteId}/editor/post/${post.id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            Edit <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                      <LayoutTemplate className="h-12 w-12 mb-4 opacity-20" />
                      <p>No posts yet.</p>
                      <Link to={`/${siteId}/editor/post/new`} className="mt-4">
                        <Button variant="outline" size="sm">
                          Create your first post
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Side Panel / Quick Tips or Stats could go here */}
            <div className="md:col-span-3 lg:col-span-2 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Tips</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-muted-foreground">
                    <p className="mb-2">
                      <strong>Writing:</strong> Use the editor to draft your
                      content with Notion-like editor.
                    </p>
                    <p className="mb-2">
                      <strong>Design:</strong> Tweak your site's appearance in
                      the Design tab to match your brand.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </PageTransition>
    </ContainerWithSidebar>
  )
}
